import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';
import { ApiClient } from './api-client.service';
import { AuthTokenService, AuthTokens } from './auth-token.service';
import { environment } from '../../../environments/environment';

export interface LoginRequest {
  tenantId: string;
  providerKind: number;
  identifier: string;
  password?: string | null;
}

export interface AdminSessionResponse {
  accountId: string;
  tenantId: string;
  branchId?: string | null;
  accessToken: string;
  expiresAtUtc?: string;
  refreshToken?: string | null;
}

export interface OtpRequest {
  phone: string;
}

export interface AdminRefreshRequest {
  tenantId: string;
  refreshToken?: string | null;
}

export interface AdminLogoutRequest {
  tenantId: string;
  refreshToken?: string | null;
}

export interface RegisterRequest {
  tenantId: string;
  branchId?: string | null;
  providerKind: number;
  identifier: string;
  password?: string | null;
  displayLabel?: string | null;
  givenLabel?: string | null;
  familyLabel?: string | null;
  skipDefaultAccessSetAssignment?: boolean;
}

export interface RegisterResponse {
  accountId: string;
  tenantId: string;
}

export interface PasswordResetRequest {
  tenantId: string;
  providerKind: number;
  identifier: string;
}

export interface DeactivateAccountResponse {
  accountId: string;
  tenantId: string;
  isDeactivated: boolean;
  deactivatedAtUtc: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private refreshInFlight$?: Observable<AuthTokens>;
  private readonly authBaseUrl = `${environment.apiBaseUrl.replace(/\/$/, '')}/api/auth`;

  constructor(
    private readonly api: ApiClient,
    private readonly http: HttpClient,
    private readonly tokenStore: AuthTokenService
  ) {}

  loginWithPassword(email: string, password: string): Observable<AdminSessionResponse> {
    const request: LoginRequest = {
      tenantId: this.getTenantId(),
      providerKind: 1,
      identifier: email,
      password
    };
    return this.http.post<AdminSessionResponse>(`${this.authBaseUrl}/admin/login`, request, { withCredentials: true }).pipe(
      tap((session) => this.tokenStore.setTokens(this.toAuthTokens(session)))
    );
  }

  loginWithOtp(phone: string, otp: string): Observable<AdminSessionResponse> {
    const request: LoginRequest = {
      tenantId: this.getTenantId(),
      providerKind: 2,
      identifier: phone,
      password: otp || null
    };
    return this.http.post<AdminSessionResponse>(`${this.authBaseUrl}/admin/login`, request, { withCredentials: true }).pipe(
      tap((session) => this.tokenStore.setTokens(this.toAuthTokens(session)))
    );
  }

  requestOtp(phone: string): Observable<void> {
    return this.api.post<void>('/api/auth/request-verification', {
      tenantId: this.getTenantId(),
      providerKind: 2,
      identifier: phone
    });
  }

  refreshTokens(): Observable<AuthTokens> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const headers = new HttpHeaders({ 'X-Skip-Auth': 'true' });
    const tenantId = this.tokenStore.getTenantId() ?? this.getTenantId();
    const refreshToken = this.tokenStore.getRefreshToken();

    this.refreshInFlight$ = this.http
      .post<AdminSessionResponse>(
        `${this.authBaseUrl}/admin/refresh`,
        { tenantId, refreshToken } as AdminRefreshRequest,
        { headers, withCredentials: true }
      )
      .pipe(
        map((session) => this.toAuthTokens(session)),
        tap((tokens) => this.tokenStore.setTokens(tokens)),
        shareReplay(1),
        finalize(() => {
          this.refreshInFlight$ = undefined;
        })
      );

    return this.refreshInFlight$;
  }

  restoreSession(): Observable<boolean> {
    if (this.tokenStore.getAccessToken()) {
      return of(true);
    }

    return this.refreshTokens().pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  logout(): Observable<void> {
    const tenantId = this.tokenStore.getTenantId() ?? this.getTenantId();
    const refreshToken = this.tokenStore.getRefreshToken();
    const headers = new HttpHeaders({ 'X-Skip-Auth': 'true' });
    return this.http.post<void>(
      `${this.authBaseUrl}/admin/logout`,
      { tenantId, refreshToken } as AdminLogoutRequest,
      { headers, withCredentials: true }
    );
  }

  deactivateAccount(): Observable<DeactivateAccountResponse> {
    return this.api.delete<DeactivateAccountResponse>('/api/accounts/self');
  }

  register(request: RegisterRequest): Observable<RegisterResponse> {
    return this.api.post<RegisterResponse>('/api/auth/register', request);
  }

  requestPasswordReset(request: PasswordResetRequest): Observable<void> {
    return this.api.post<void>('/api/auth/request-reset', request);
  }

  private getTenantId(): string {
    return environment.defaultTenantId;
  }

  private toAuthTokens(session: AdminSessionResponse): AuthTokens {
    return {
      tenantId: session.tenantId,
      branchId: session.branchId ?? undefined,
      accessToken: session.accessToken,
      expiresAtUtc: session.expiresAtUtc,
      refreshToken: session.refreshToken ?? undefined
    };
  }
}
